/**
 * Language routing helpers for KosMedico.
 *
 * The site is served under three language prefixes:
 *   - "/"     → German (default, canonical)
 *   - "/pt"   → Portuguese
 *   - "/en"   → English
 *
 * All internal page slugs stay the same across languages EXCEPT the prices
 * page, which has a localized slug per requirements:
 *   - DE: /preise
 *   - PT: /precos
 *   - EN: /prices
 *
 * The Header/Footer nav uses `useLocalizedPath` so links transparently
 * include the current language prefix and the correct prices slug.
 */

export const LANGS = ["de", "pt", "en"];

export const DEFAULT_LANG = "de";

/** Extracts the language from a pathname. Falls back to DE for "/" and unknown prefixes. */
export const langFromPath = (pathname = "/") => {
  const seg = pathname.split("/").filter(Boolean)[0];
  return seg === "pt" || seg === "en" ? seg : "de";
};

/** Root prefix for a language ("" for DE, "/pt", "/en"). */
export const prefixFor = (lang) => (lang === "de" ? "" : `/${lang}`);

/** Localized prices slug per language. */
export const PRICES_SLUG = { de: "preise", pt: "precos", en: "prices" };

/** Named page keys mapped to per-language relative paths (without prefix). */
const PAGE_PATHS = {
  home: () => "",
  treatments: () => "/tratamentos",
  treatment: (_lang, slug) => `/tratamentos/${slug}`,
  treatmentDirect: (_lang, slug) => `/${slug}`,
  about: () => "/sobre",
  faq: () => "/faq",
  contact: () => "/contato",
  prices: (lang) => `/${PRICES_SLUG[lang]}`,
  impressum: () => "/impressum",
  agb: () => "/agb",
  datenschutz: () => "/datenschutz",
};

/** Build a fully-qualified localized path from a page key. */
export const buildLocalizedPath = (page, lang, param) => {
  const builder = PAGE_PATHS[page];
  const rel = builder ? builder(lang, param) : "";
  const p = prefixFor(lang) + rel;
  return p === "" ? "/" : p;
};

/** Prepend the current language prefix to an absolute in-app path. */
export const withLangPrefix = (path, lang) => {
  const prefix = prefixFor(lang);
  if (!prefix) return path;
  return `${prefix}${path.startsWith("/") ? path : `/${path}`}`;
};

/** Strip a leading language prefix off a pathname (used for equivalent-page mapping). */
export const stripLangPrefix = (pathname) => {
  const lang = langFromPath(pathname);
  const prefix = prefixFor(lang);
  if (!prefix) return { lang, rest: pathname };
  return { lang, rest: pathname.slice(prefix.length) || "/" };
};

/**
 * Map a pathname from one language to another, preserving the page identity
 * (including the localized prices slug).
 */
export const translatePath = (pathname, targetLang) => {
  const { rest } = stripLangPrefix(pathname);
  // Prices slug swap
  const priceMatch = /^\/(preise|precos|prices)(\/.*)?$/.exec(rest);
  if (priceMatch) {
    const tail = priceMatch[2] || "";
    return withLangPrefix(`/${PRICES_SLUG[targetLang]}${tail}`, targetLang);
  }
  if (rest === "/" || rest === "") {
    return withLangPrefix("/", targetLang);
  }
  return withLangPrefix(rest, targetLang);
};
